import {
  defaultTextMarks,
  getRangeCharacterOffset,
  getSelectedLineIndices,
  htmlToRuns,
  normalizeMarkColor,
  normalizeFontSize,
  resolveLineAlign,
  resolveParagraphAlign,
  setSelectionCharacterRange,
  WCP_TAB_ATTR,
} from "../../../../utils/articleParagraphUtils";

export const TAB_CHARACTER = "\t";

export const figmaPxToRem = (figmaPx) => `${(figmaPx * 0.85) / 14}rem`;

const restoreSelectionRange = (editorElement, savedRange) => {
  if (!savedRange || !editorElement) {
    return;
  }
  const selection = window.getSelection();
  if (!selection) {
    return;
  }
  if (!editorElement.contains(savedRange.startContainer)) {
    return;
  }
  selection.removeAllRanges();
  selection.addRange(savedRange);
};

export const insertTabAtSelection = (editorElement) => {
  if (!editorElement) {
    return false;
  }
  editorElement.focus();
  const selection = window.getSelection();
  if (!selection?.rangeCount) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!editorElement.contains(range.commonAncestorContainer)) {
    return false;
  }
  if (!range.collapsed) {
    range.deleteContents();
  }

  const tabSpan = document.createElement("span");
  tabSpan.setAttribute(WCP_TAB_ATTR, "1");
  tabSpan.textContent = "\t";
  range.insertNode(tabSpan);
  range.setStartAfter(tabSpan);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

export const insertTextAtSelection = (editorElement, text) => {
  if (!editorElement || text == null) {
    return false;
  }
  editorElement.focus();
  return document.execCommand("insertText", false, text);
};

const getTextSegmentsInRange = (range) => {
  const ancestor = range.commonAncestorContainer;
  const root = ancestor.nodeType === Node.TEXT_NODE ? ancestor.parentNode : ancestor;
  if (!root) {
    return [];
  }

  const segments = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (range.intersectsNode(node)) {
      const text = node.textContent ?? "";
      if (text) {
        let startOffset = 0;
        let endOffset = text.length;
        if (node === range.startContainer) {
          startOffset = range.startOffset;
        }
        if (node === range.endContainer) {
          endOffset = range.endOffset;
        }
        if (startOffset < endOffset) {
          segments.push({ node, startOffset, endOffset });
        }
      }
    }
    node = walker.nextNode();
  }
  return segments;
};

const applyStyleToTextSegment = ({ node, startOffset, endOffset }, styleProperties) => {
  let target = node;
  if (startOffset > 0) {
    target = target.splitText(startOffset);
  }
  const segmentLength = endOffset - startOffset;
  if (segmentLength < target.length) {
    target.splitText(segmentLength);
  }

  const span = document.createElement("span");
  Object.entries(styleProperties).forEach(([key, value]) => {
    span.style[key] = value;
  });
  target.parentNode?.insertBefore(span, target);
  span.appendChild(target);
};

const applyTypingInlineStyle = (editorElement, range, styleProperties) => {
  const selection = window.getSelection();
  if (!selection) {
    return false;
  }

  const span = document.createElement("span");
  Object.entries(styleProperties).forEach(([key, value]) => {
    span.style[key] = value;
  });
  const marker = document.createTextNode("\u200B");
  span.appendChild(marker);
  range.insertNode(span);

  const nextRange = document.createRange();
  nextRange.setStart(marker, 1);
  nextRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(nextRange);
  editorElement.focus();
  return true;
};

export const saveEditorSelection = (editorElement) => {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !editorElement) {
    return null;
  }
  const range = selection.getRangeAt(0);
  if (!editorElement.contains(range.commonAncestorContainer)) {
    return null;
  }
  return range.cloneRange();
};

export const restoreEditorSelection = (editorElement, savedRange) => {
  if (!savedRange || !editorElement) {
    return false;
  }
  restoreSelectionRange(editorElement, savedRange);
  editorElement.focus();
  return true;
};

export const applyInlineStyleToRange = (editorElement, range, styleProperties) => {
  if (!editorElement || !range || !editorElement.contains(range.commonAncestorContainer) || range.collapsed) {
    return false;
  }

  const segments = getTextSegmentsInRange(range);
  if (!segments.length) {
    return false;
  }

  const startOffset = getRangeCharacterOffset(editorElement, range.startContainer, range.startOffset);
  const endOffset = getRangeCharacterOffset(editorElement, range.endContainer, range.endOffset);
  segments.reverse().forEach((segment) => {
    applyStyleToTextSegment(segment, styleProperties);
  });

  if (typeof startOffset === "number" && typeof endOffset === "number") {
    setSelectionCharacterRange(editorElement, startOffset, endOffset);
  }
  return true;
};

export const applyInlineStyleToSelection = (editorElement, styleProperties, savedRange = null) => {
  if (!editorElement) {
    return false;
  }

  let range = savedRange;
  if (!range) {
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return false;
    }
    range = selection.getRangeAt(0);
  } else {
    restoreEditorSelection(editorElement, savedRange);
    range = savedRange;
  }

  if (!editorElement.contains(range.commonAncestorContainer)) {
    return false;
  }

  if (range.collapsed) {
    return applyTypingInlineStyle(editorElement, range, styleProperties);
  }

  return applyInlineStyleToRange(editorElement, range, styleProperties);
};

export const applyFontSizeToSelection = (editorElement, figmaPx, savedRange = null) =>
  applyInlineStyleToSelection(editorElement, { fontSize: figmaPxToRem(figmaPx) }, savedRange);

export const applyColorToSelection = (editorElement, color, savedRange = null) =>
  applyInlineStyleToSelection(editorElement, { color }, savedRange);

export const execEditorCommand = (editorElement, command, value) => {
  if (!editorElement) {
    return false;
  }
  const selection = window.getSelection();
  const savedRange = selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
  const startOffset = savedRange
    ? getRangeCharacterOffset(editorElement, savedRange.startContainer, savedRange.startOffset)
    : null;
  const endOffset = savedRange
    ? getRangeCharacterOffset(editorElement, savedRange.endContainer, savedRange.endOffset)
    : null;
  editorElement.focus();
  document.execCommand("styleWithCSS", false, true);
  const applied = document.execCommand(command, false, value);
  if (typeof startOffset === "number" && typeof endOffset === "number") {
    setSelectionCharacterRange(editorElement, startOffset, endOffset);
  } else if (savedRange && editorElement.contains(savedRange.startContainer)) {
    restoreSelectionRange(editorElement, savedRange);
  }
  return applied;
};

export const readSelectionFormatState = (editorElement) => {
  if (!editorElement) {
    return { bold: false, italic: false };
  }

  const selectionInEditor =
    window.getSelection()?.rangeCount &&
    editorElement.contains(window.getSelection().getRangeAt(0).commonAncestorContainer);

  if (editorElement.contains(document.activeElement) && selectionInEditor) {
    return {
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
    };
  }

  const marks = readSelectionMarksFromEditor(editorElement);
  return {
    bold: marks?.weight === "bold",
    italic: marks?.style === "italic",
  };
};

export const hasEditorCaret = (editorElement) => {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !editorElement) {
    return false;
  }
  const range = selection.getRangeAt(0);
  return editorElement.contains(range.commonAncestorContainer) && range.collapsed;
};

export const hasEditorTextSelection = (editorElement) => {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !editorElement) {
    return false;
  }
  const range = selection.getRangeAt(0);
  return editorElement.contains(range.commonAncestorContainer) && !range.collapsed;
};

export const hasEditorFormatContext = (editorElement) =>
  hasEditorTextSelection(editorElement) || hasEditorCaret(editorElement);

const readMarksAtRangeBoundary = (editorElement, range) => {
  const probe = document.createElement("div");
  const preRange = document.createRange();
  preRange.selectNodeContents(editorElement);
  preRange.setEnd(range.startContainer, range.startOffset);
  probe.appendChild(preRange.cloneContents());
  const runs = htmlToRuns(probe).filter((run) => run.text.length > 0);
  if (!runs.length) {
    return defaultTextMarks();
  }
  return {
    ...defaultTextMarks(),
    ...(runs[runs.length - 1].marks || {}),
  };
};

export const readSelectionMarksFromEditor = (editorElement) => {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !editorElement) {
    return null;
  }
  const range = selection.getRangeAt(0);
  if (!editorElement.contains(range.commonAncestorContainer)) {
    return null;
  }

  if (range.collapsed) {
    return readMarksAtRangeBoundary(editorElement, range);
  }

  const probe = document.createElement("div");
  probe.appendChild(range.cloneContents());
  const runs = htmlToRuns(probe).filter((run) => run.text.length > 0);
  if (!runs.length) {
    return readMarksAtRangeBoundary(editorElement, range);
  }
  return {
    ...defaultTextMarks(),
    ...(runs[0].marks || {}),
  };
};

export const readSelectionToolbarState = (editorElement, block) => {
  const hasTextSelection = hasEditorTextSelection(editorElement);
  const hasCaret = hasEditorCaret(editorElement);
  const formatActive = hasTextSelection || hasCaret;
  const format = formatActive ? readSelectionFormatState(editorElement) : { bold: false, italic: false };
  const marks = formatActive ? readSelectionMarksFromEditor(editorElement) : null;
  const lineIndices = hasTextSelection ? getSelectedLineIndices(editorElement) : [];
  let align = block ? resolveParagraphAlign(block) : null;
  if (lineIndices.length > 0 && block) {
    const aligns = lineIndices.map((index) => resolveLineAlign(block, index));
    align = aligns.every((value) => value === aligns[0]) ? aligns[0] : null;
  }
  return {
    ...format,
    fontSize: normalizeFontSize(marks?.fontSize) || null,
    color: normalizeMarkColor(marks?.color) || marks?.color || null,
    hasTextSelection,
    hasEditorCaret: hasCaret,
    formatActive,
    align,
  };
};

export { getSelectedLineIndices } from "../../../../utils/articleParagraphUtils";
